import { Link } from "react-router-dom";
import {
  ArrowRight, Globe, MagnetIcon, Workflow, BarChart3, Bot, Users,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import FAQ from "@/components/FAQ";
import HeroBrandMark from "@/components/HeroBrandMark";

const SERVICES_IMG = "https://static.prod-images.emergentagent.com/jobs/6461294c-b3c3-4056-9836-e7bb045d737d/images/3ac17b21d7295e1625741426e60ad5e87274e860d84aa3927028a006a3aebab2.png";

const PILLARS = [
  {
    icon: Globe,
    n: "01",
    title: "Modern Website",
    pitch: "A site engineered to convert, not just look the part.",
    body: "Fast. Mobile-first. Built around the way your customers actually search and book. Local SEO foundations, conversion-focused page structure, integrated quote forms, click-to-call, and review snippets.",
    included: [
      "Custom design, branded to your business",
      "Local SEO foundation (Google Business, schema, locations)",
      "Quote forms, click-to-call, after-hours capture",
      "Hosting, security, ongoing performance tuning",
    ],
    outcome: "More of your existing traffic turns into booked jobs, same ad spend, more revenue.",
  },
  {
    icon: MagnetIcon,
    n: "02",
    title: "Lead Capture & Funnels",
    pitch: "Every lead, every channel, in one place.",
    body: "Web forms, phone calls, missed calls, Facebook / Google ads, referrals, captured, tagged and routed instantly. The leak between marketing spend and booked work gets sealed.",
    included: [
      "Web form integration & smart routing",
      "Missed-call SMS auto-response",
      "Ad-platform lead sync (Google, Meta)",
      "Source tagging & attribution reporting",
    ],
    outcome: "Zero leads lost. Every dollar of marketing spend traceable to revenue.",
  },
  {
    icon: Workflow,
    n: "03",
    title: "CRM & Follow-up Workflows",
    pitch: "The follow-up that wins the work, without you remembering.",
    body: "A CRM built around how your trade actually operates: quote sent → 24h reminder → 72h call → 7-day final follow-up. Automated SMS, email, and tasks for the team. Wins the jobs you currently lose to silence.",
    included: [
      "CRM configured to your job lifecycle",
      "Automated quote and review follow-up sequences",
      "Team task assignments & reminders",
      "Customer history, notes, photos all in one record",
    ],
    outcome: "Quote-to-job conversion typically lifts 2–4× within 90 days.",
  },
  {
    icon: BarChart3,
    n: "04",
    title: "KPI Dashboards",
    pitch: "One screen. The whole business.",
    body: "Real-time visibility into leads, quotes, conversion, revenue, job profitability, team performance and review velocity. The numbers an owner needs, without opening five tabs or asking the bookkeeper.",
    included: [
      "Live dashboard (web & mobile)",
      "Lead → quote → job → revenue funnel",
      "Margin & profitability by job type",
      "Team performance & review tracking",
    ],
    outcome: "You manage by signal, not by feel. Decisions get faster, sharper, more profitable.",
  },
  {
    icon: Bot,
    n: "05",
    title: "AI Support",
    pitch: "A trained assistant that handles the easy stuff so you don't have to.",
    body: "An AI assistant, web chat and (optional) phone, trained on your services, pricing tiers, service area and FAQs. Qualifies leads, books jobs, captures the after-hours enquiries you currently miss.",
    included: [
      "Custom-trained web chat assistant",
      "Optional AI voice receptionist for after-hours",
      "FAQ knowledge base & job-booking flow",
      "Handover-to-human rules for complex enquiries",
    ],
    outcome: "After-hours capture up. Owner / partner phone-time down.",
  },
  {
    icon: Users,
    n: "06",
    title: "Outsourced Execution Team",
    pitch: "The team that actually runs it.",
    body: "Once the system is built, our team operates it day-to-day, quote follow-up calls, CRM hygiene, ad management, content posting, review chasing, monthly reporting. You stay on the tools.",
    included: [
      "Dedicated account lead (your point of contact)",
      "Daily CRM hygiene & follow-up calls",
      "Ad campaign management & weekly review",
      "Content posting, review chasing, reporting",
    ],
    outcome: "60–80% reduction in owner / partner admin hours. The system runs whether you're there or not.",
  },
];

const FAQS = [
  {
    q: "Can I take just one or two pillars instead of the full system?",
    a: "Possibly, but we're cautious about it. The pillars compound: a beautiful site without lead capture, or capture without CRM follow-up, leaves the biggest gains on the table. We'll be honest on the audit call about whether a partial scope is worth doing.",
  },
  {
    q: "Do I own the website, CRM and data?",
    a: "Yes. Always. Your domain, your site, your CRM account, your customer data. If you ever stop working with us, everything stays yours and continues operating.",
  },
  {
    q: "What tools do you use under the hood?",
    a: "We use a curated stack of best-in-class platforms (CRM, dashboarding, AI, ad platforms) selected for reliability and Australian operator-friendliness. We share the exact stack during the audit so there are no surprises.",
  },
  {
    q: "How fast do the dashboards update?",
    a: "Most KPIs are near real-time (under 5 minutes). Financial-margin metrics that depend on your accounting system sync nightly. The audit identifies any gaps before we quote.",
  },
  {
    q: "Will the AI assistant sound like a robot?",
    a: "No. It's trained on your business language, your tone, your pricing logic. You sign off on the script before it goes live. If a customer wants a human, the handover is instant.",
  },
];

export default function Services() {
  return (
    <>
      <section className="relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0">
          <img src={SERVICES_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/90 to-ink" />
        </div>
        <div className="relative container-x pt-24 md:pt-32 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <div className="overline">Flagship product · Division 01, Growth Systems</div>
              <h1 className="mt-6 max-w-3xl font-display font-light tracking-tight text-balance text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02]">
                Built in <span className="text-gold">60</span>. Run by us.
              </h1>
              <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
                The Toulios Growth System. Six integrated components, installed
                in 60 days, operated by us from week 8. The fastest, cleanest
                path to a business that compounds while you&rsquo;re focused on
                the work that matters.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="btn-gold" data-testid="services-cta-book">
                  Book a Strategy Call <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/how-it-works" className="btn-ghost">See the build process</Link>
              </div>
            </div>
            <div className="hidden lg:flex lg:col-span-4 items-center justify-center">
              <HeroBrandMark variant="mark" />
            </div>
          </div>
        </div>
      </section>

      {/* PILLAR SECTIONS */}
      <section className="section-y" data-testid="services-pillars">
        <div className="container-x space-y-24 md:space-y-32">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-testid={`service-pillar-${i}`}>
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3">
                    <span className="overline">{p.n}</span>
                    <span className="hairline w-16" />
                  </div>
                  <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-display font-light tracking-tight text-balance">
                    {p.title}
                  </h2>
                  <p className="mt-6 text-lg text-gold leading-relaxed">{p.pitch}</p>
                  <p className="mt-5 text-zinc-400 leading-relaxed">{p.body}</p>
                </div>

                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.05]">
                  <div className="bg-ink p-6 md:p-8">
                    <p.icon className="w-5 h-5 text-gold" />
                    <div className="mt-6 overline">What&rsquo;s included</div>
                    <ul className="mt-4 space-y-3 text-sm text-zinc-300">
                      {p.included.map((b) => (
                        <li key={b} className="flex items-start gap-3">
                          <span className="mt-2 inline-block w-1 h-1 bg-gold flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-ink p-6 md:p-8">
                    <div className="overline">What changes for you</div>
                    <p className="mt-6 text-zinc-200 leading-relaxed text-[15px]">{p.outcome}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="services-faq-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Common questions"
              title="On the services."
              description="Quick answers to what most operators want to know before booking an audit."
            />
          </div>
          <div className="lg:col-span-7">
            <FAQ items={FAQS} testIdPrefix="services-faq" />
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
